import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { Sun, Moon, Monitor, Globe, ChevronRight, Check, CreditCard, Settings, LogOut, MessageCircle, ChevronDown, Code } from "lucide-react";
import { isApiUsageVisible } from "components/SideMenu/useMenuItems";
import { useTheme, Theme } from "hooks/useTheme";
import ProgressBar from "components/ProgressBar";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { PATHS } from "router/config/paths";
import { useAppDispatch, useAppSelector } from "store";
import { EventDispatchLogout } from "router/Router";
import { setAccountSelectedMenu, setCredits, setUser } from "store/commonSlice";
import { AccountSelectMenu } from "pages/Account/Account";
import userService from "api/user/user.service";
import developerService from "api/developer/developer.service";
import { AnalyticsEvent } from "utils";
import i18n from "i18n";
import { SUPPORTED_LOCALES, SupportedLocale, isSupportedLocale } from "i18n/config";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";

const LANGUAGE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  tr: "Türkçe",
  es: "Español",
  pt: "Português",
  it: "Italiano",
  fr: "Français",
  hi: "हिन्दी",
};

type Submenu = "language" | "theme" | null;

const UserProfileSidebar = ({ collapsed, variant = "sidebar" }: { collapsed?: boolean; variant?: "sidebar" | "topbar" }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n: i18nInstance } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const [submenu, setSubmenu] = useState<Submenu>(null);
  const isTopbar = variant === "topbar";

  const { refs, floatingStyles } = useFloating({
    // Sidebar variant sits at the bottom-left corner → open upward. Topbar variant sits at the
    // top-right → open downward, right-aligned so the popover stays inside the viewport.
    placement: isTopbar ? "bottom-end" : "top-start",
    // Topbar dropdown hangs at the same height as the What's New panel (both offset 12).
    middleware: [offset(isTopbar ? 12 : 4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  // Language/Theme flyouts open to the LEFT of the dropdown (it hugs the right edge of the screen).
  const submenuFloating = useFloating({
    placement: "left-start",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const { user, credits } = useAppSelector((state) => state.common);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [user.profilePhoto]);

  // Credits are cached in Redux (state.common.credits) so the dropdown paints them instantly on
  // open instead of flashing empty while GET /user-detail resolves. Preload once per session, then
  // refresh on every open so the numbers stay current without the flash.
  const fetchCredits = useCallback(() => {
    if (!user.id) return;

    userService.getUserDetail({ user_id: user.id }).then((res) => {
      // 401/403/409/503 resolve with responseData: null + _handled: true (see HttpClient interceptor / CLAUDE.md Trap #1).
      if (res?._handled || !res?.responseData) return;

      dispatch(setUser({ profilePhoto: res.responseData.user_info?.profile_photo ?? "" }));
      const ci = res.responseData.credit_info;

      dispatch(
        setCredits({
          design: { total: ci?.refabric_credits?.total ?? 0, used: ci?.refabric_credits?.used ?? 0 },
          // One-time add-on credits — pre-aggregated by the BE, separate from the monthly pool (REF-5450).
          designBonus: res.responseData.bonus_credits?.find((item) => item.action_code === "refabric_credits")?.remaining ?? 0,
          photoshoot: { total: ci?.model_credits?.total ?? 0, used: ci?.model_credits?.used ?? 0 },
        })
      );
    });
  }, [user.id, dispatch]);

  useEffect(() => {
    if (!credits.loaded) fetchCredits();
  }, [credits.loaded, fetchCredits]);

  useEffect(() => {
    if (showMenu) fetchCredits();
    else setSubmenu(null);
  }, [showMenu, fetchCredits]);

  // P4-F2 (STRIPE Phase 4): "Remaining" is the spend gate's own answer — `GET /v1/account/balance` → `remaining`, the
  // figure Plan & Billing shows as "Available to use now" (team pool, member ceiling and extra credits included). It is
  // never `total − used`: that ignores the team pool, the member ceiling and usage on lapsed buckets. Unknown → no figure.
  const [spendable, setSpendable] = useState<Record<string, number>>({});
  // Each open is one read; only the live one may write, so a slow answer from an earlier or closed open can't land.
  const balanceReadRef = useRef(0);

  useEffect(() => {
    // Another account (or a logout) must never inherit the previous one's figure.
    balanceReadRef.current += 1;
    setSpendable({});
  }, [user.id]);

  useEffect(() => {
    if (!showMenu || !user.id) return;

    const read = ++balanceReadRef.current;
    developerService
      .getBalance()
      .then((res) => {
        if (read !== balanceReadRef.current) return;
        // Same rule as PlanBilling: a handled or malformed answer means unknown, and the last figure is withdrawn ([HTTP_HANDLED_RESPONSE]).
        if (!res || ("_handled" in res && res._handled) || !("balances" in res) || !Array.isArray(res.balances)) {
          setSpendable({});

          return;
        }
        setSpendable(Object.fromEntries(res.balances.filter((b) => typeof b.remaining === "number").map((b) => [b.credit_type, b.remaining])));
      })
      .catch(() => {
        if (read === balanceReadRef.current) setSpendable({});
      });

    // Closing the menu, switching account or unmounting retires this read: its answer may no longer write.
    return () => {
      balanceReadRef.current += 1;
    };
  }, [showMenu, user.id]);

  // Close on outside click — the main popover AND the portaled submenu flyout both count as "inside".
  useEffect(() => {
    if (!showMenu) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = refs.reference.current instanceof HTMLElement && refs.reference.current.contains(target);
      const inPopover = refs.floating.current instanceof HTMLElement && refs.floating.current.contains(target);
      const inSubmenu = submenuFloating.refs.floating.current instanceof HTMLElement && submenuFloating.refs.floating.current.contains(target);
      if (!inTrigger && !inPopover && !inSubmenu) setShowMenu(false);
    };

    document.addEventListener("mousedown", handleMouseDown);

    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showMenu, refs.reference, refs.floating, submenuFloating.refs.floating]);

  const menuItems = [
    {
      icon: Settings,
      title: t("common:sidebar.account_settings"),
      onClick: () => {
        dispatch(setAccountSelectedMenu(AccountSelectMenu.AccountSettings));
        navigate(PATHS.ACCOUNT);
      },
    },
    {
      icon: CreditCard,
      title: t("common:sidebar.plans_billing"),
      onClick: () => {
        dispatch(setAccountSelectedMenu(AccountSelectMenu.PlanBilling));
        navigate(PATHS.ACCOUNT);
      },
    },
    {
      icon: MessageCircle,
      title: t("common:sidebar.support"),
      onClick: () => {
        dispatch(setAccountSelectedMenu(AccountSelectMenu.Support));
        navigate(PATHS.ACCOUNT);
      },
    },
    ...(isApiUsageVisible(user.permissions) ? [{ icon: Code, title: t("common:nav.developers"), onClick: () => navigate(PATHS.DEVELOPERS) }] : []),
  ];

  // Theme selector — icons/labels reused in the row value and the flyout.
  const themeOptions: { id: Theme; icon: typeof Sun; label: string }[] = [
    { id: "light", icon: Sun, label: t("common:theme_toggle.options.light") },
    { id: "dark", icon: Moon, label: t("common:theme_toggle.options.dark") },
    { id: "system", icon: Monitor, label: t("common:theme_toggle.options.system") },
  ];
  const currentTheme = themeOptions.find((o) => o.id === theme) ?? themeOptions[2];
  const CurrentThemeIcon = currentTheme.icon;
  const currentLocale = i18nInstance.language as SupportedLocale;
  const currentLanguageLabel = LANGUAGE_LABELS[currentLocale] ?? currentLocale;

  // The bar keeps showing the plan pool from GET /users/{id}, as before; the "Remaining" figure is the spend gate's (above).
  const designPoolLeft = credits.design.total - credits.design.used;
  const photoshootPoolLeft = credits.photoshoot.total - credits.photoshoot.used;

  const handleTriggerClick = () => {
    AnalyticsEvent("profile_avatar_click", "", "click");
    setShowMenu((prev) => !prev);
  };

  const handleLogout = () => {
    AnalyticsEvent("logout_click", "", "click");
    window.dispatchEvent(new CustomEvent(EventDispatchLogout));
    setShowMenu(false);
  };

  const changeLanguage = (next: SupportedLocale) => {
    setSubmenu(null);
    if (!isSupportedLocale(next) || next === i18nInstance.language) return;

    i18n.changeLanguage(next);
    dispatch(setUser({ language: next }));
    trackEvent(EVENTS.USER.LANGUAGE_CHANGED, { language: next });
    // Best-effort BE persist (localStorage stays authoritative until BE stores `language`).
    if (user.id) userService.editUserDetail(user.id, { language: next } as any).catch(() => undefined);
  };

  const toggleSubmenu = (which: Exclude<Submenu, null>) => setSubmenu((prev) => (prev === which ? null : which));

  const avatarContent =
    user.profilePhoto && !imgError ? (
      <img src={user.profilePhoto} alt={t("common:sidebar.profile_alt")} className="w-full h-full rounded-full object-cover" onError={() => setImgError(true)} />
    ) : (
      (user.name?.[0] ?? user.email?.[0] ?? "U").toUpperCase()
    );

  // One balance card in the screenshot style: title, progress bar, then Total / Remaining (+ optional Extra) rows.
  const renderCreditCard = (key: string, title: string, total: number, poolLeft: number, opts?: { bonus?: number }) => (
    <div key={key} className="flex flex-col gap-1.5 rounded-lg bg-black-200/40 dark:bg-black-100 p-2.5">
      <span className="text-menu text-trueBlack dark:text-grey">{title}</span>
      <ProgressBar trackClassName="bg-black-300" progressBarClassName="!bg-trueBlack dark:!bg-white" maxAmount={total} currentAmount={poolLeft} />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] text-grey">{t("common:sidebar.total")}</span>
        <span className="text-[12px] text-trueBlack dark:text-white">{total.toLocaleString()}</span>
      </div>
      {spendable[key] !== undefined && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] text-grey">{t("common:sidebar.remaining")}</span>
          <span className="text-[12px] text-trueBlack dark:text-white" data-testid={`profile-balance-remaining-${key}`}>
            {spendable[key].toLocaleString()}
          </span>
        </div>
      )}
      {opts?.bonus && opts.bonus > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] text-grey">{t("common:sidebar.extra_credits")}</span>
          <span className="text-[12px] text-green-dark dark:text-green">+{opts.bonus.toLocaleString()}</span>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="relative">
      {isTopbar ? (
        // Topbar variant: avatar-only trigger, sized just under the Academy / Agent buttons.
        <button
          ref={refs.setReference}
          className={clsx(
            "flex-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-xs font-medium uppercase overflow-hidden border border-black-300 transition-all",
            showMenu ? "ring-2 ring-trueBlack dark:ring-white" : "hover:brightness-95"
          )}
          title={user.name || user.email || t("common:sidebar.user_fallback")}
          onClick={handleTriggerClick}
        >
          {avatarContent}
        </button>
      ) : (
        <div
          ref={refs.setReference}
          className={clsx(
            "flex items-center gap-2 p-2 rounded border border-black-300 cursor-pointer text-trueBlack dark:text-grey hover:bg-black-200 transition-colors",
            collapsed && "justify-center",
            showMenu && "bg-black-200"
          )}
          title={collapsed ? user.name || user.email || t("common:sidebar.user_fallback") : undefined}
          onClick={handleTriggerClick}
        >
          <div className="flex-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium flex-shrink-0 uppercase">{avatarContent}</div>
          {!collapsed && (
            <>
              <span className="text-menu truncate">{user.name || user.email || t("common:sidebar.user_fallback")}</span>
              <ChevronDown className={clsx("w-[14px] h-[14px] ml-auto flex-shrink-0 transition-transform", showMenu ? "rotate-180" : "")} />
            </>
          )}
        </div>
      )}

      {showMenu &&
        createPortal(
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 999999 }} className="flex flex-col w-60 rounded-xl bg-trueWhite dark:bg-black border border-black-300 shadow-xl">
            {/* Identity — avatar + full name over email */}
            <div className="flex items-center gap-2.5 px-3 pt-3 pb-1">
              <div className="flex-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-medium uppercase overflow-hidden border border-black-300 flex-shrink-0">
                {avatarContent}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-menu text-trueBlack dark:text-grey truncate">{user.name || user.email || t("common:sidebar.user_fallback")}</span>
                {user.name && <span className="text-[12px] text-grey truncate">{user.email}</span>}
              </div>
            </div>

            {/* Balance — up to two cards (design + model), screenshot layout */}
            <div className="flex flex-col gap-1.5 p-2">
              {!credits.loaded ? (
                <div className="flex flex-col gap-2 rounded-lg bg-black-200/40 dark:bg-black-100 p-3">
                  <div className="h-3 w-1/3 rounded bg-black-300 animate-pulse" />
                  <div className="h-2 w-full rounded bg-black-300 animate-pulse" />
                  <div className="h-2 w-2/3 rounded bg-black-300 animate-pulse" />
                </div>
              ) : (
                <>
                  {/* A card also shows when only extra credits are spendable (no plan pool). */}
                  {(credits.design.total > 0 || spendable.refabric_credits > 0) &&
                    renderCreditCard("refabric_credits", t("common:sidebar.refabric_credits"), credits.design.total, designPoolLeft, { bonus: credits.designBonus })}
                  {(credits.photoshoot.total > 0 || spendable.model_credits > 0) && renderCreditCard("model_credits", t("common:sidebar.model_credits"), credits.photoshoot.total, photoshootPoolLeft)}
                </>
              )}
            </div>

            <div className="h-px bg-black-300" />

            {/* Account menu */}
            <div className="flex flex-col py-1">
              {menuItems.map((item, index) => (
                <button
                  key={`SidebarProfileMenuItem_${index}`}
                  className="flex items-center gap-2.5 px-4 py-2 text-menu text-trueBlack dark:text-grey hover:bg-black-200/60 dark:hover:text-white transition-colors"
                  onClick={() => {
                    item.onClick();
                    setShowMenu(false);
                  }}
                >
                  <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                  <span>{item.title}</span>
                </button>
              ))}

              {/* Language — opens a flyout to the left */}
              <button
                ref={submenu === "language" ? submenuFloating.refs.setReference : undefined}
                className={clsx(
                  "flex items-center gap-2.5 px-4 py-2 text-menu text-trueBlack dark:text-grey hover:bg-black-200/60 dark:hover:text-white transition-colors",
                  submenu === "language" && "bg-black-200/60"
                )}
                onClick={() => toggleSubmenu("language")}
              >
                <Globe className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
                <span>{t("common:sidebar.language_label")}</span>
                <span className="ml-auto flex items-center gap-1 text-grey">
                  <span className="text-bodySm">{currentLanguageLabel}</span>
                  <ChevronRight className="w-4 h-4" />
                </span>
              </button>

              {/* Theme — opens a flyout to the left (topbar avatar only; the mobile drawer has its own ThemeToggle) */}
              {isTopbar && (
                <button
                  ref={submenu === "theme" ? submenuFloating.refs.setReference : undefined}
                  className={clsx(
                    "flex items-center gap-2.5 px-4 py-2 text-menu text-trueBlack dark:text-grey hover:bg-black-200/60 dark:hover:text-white transition-colors",
                    submenu === "theme" && "bg-black-200/60"
                  )}
                  onClick={() => toggleSubmenu("theme")}
                >
                  <CurrentThemeIcon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
                  <span>{t("common:sidebar.theme_label")}</span>
                  <span className="ml-auto flex items-center gap-1 text-grey">
                    <span className="text-bodySm">{currentTheme.label}</span>
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </button>
              )}
            </div>

            <div className="h-px bg-black-300" />

            {/* Sign out */}
            <button className="flex items-center gap-2.5 px-4 py-2.5 text-menu text-trueBlack dark:text-grey hover:text-orange dark:hover:text-orange transition-colors" onClick={handleLogout}>
              <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
              <span>{t("common:sidebar.logout")}</span>
            </button>
          </div>,
          document.body
        )}

      {/* Language / Theme flyout — portaled so it escapes the dropdown's rounded clip; opens left. */}
      {showMenu &&
        submenu &&
        createPortal(
          <div
            ref={submenuFloating.refs.setFloating}
            style={{ ...submenuFloating.floatingStyles, zIndex: 1000000 }}
            className="flex flex-col gap-0.5 w-[180px] rounded-lg bg-trueWhite dark:bg-black border border-black-300 shadow-xl p-1"
          >
            {submenu === "language"
              ? SUPPORTED_LOCALES.map((locale) => {
                  const isActive = locale === currentLocale;

                  return (
                    <button
                      key={locale}
                      className={clsx(
                        "flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-menu transition-colors",
                        isActive ? "text-trueBlack dark:text-white" : "text-trueBlack dark:text-grey hover:bg-black-200/60"
                      )}
                      onClick={() => changeLanguage(locale)}
                    >
                      <span>{LANGUAGE_LABELS[locale]}</span>
                      {isActive && <Check className="ml-auto w-4 h-4 text-green-dark dark:text-green" />}
                    </button>
                  );
                })
              : themeOptions.map((option) => {
                  const Icon = option.icon;
                  const isActive = theme === option.id;

                  return (
                    <button
                      key={option.id}
                      className={clsx(
                        "flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-menu transition-colors",
                        isActive ? "text-trueBlack dark:text-white" : "text-trueBlack dark:text-grey hover:bg-black-200/60"
                      )}
                      onClick={() => {
                        AnalyticsEvent("Color_Theme", "", "click", { panel: option.id });
                        setTheme(option.id);
                        setSubmenu(null);
                      }}
                    >
                      <Icon size={16} strokeWidth={1.75} />
                      <span>{option.label}</span>
                      {isActive && <Check className="ml-auto w-4 h-4 text-green-dark dark:text-green" />}
                    </button>
                  );
                })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default UserProfileSidebar;
